/*!
 * AudoChat - Billing Page for Customers
 */

// @mui material components
import Grid from "@mui/material/Grid";
import { Card } from "@mui/material";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiProgress from "components/VuiProgress";

// Vision UI Dashboard React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import linearGradient from "assets/theme/functions/linearGradient";

// Vision UI Dashboard React base styles
import colors from "assets/theme/base/colors";

// React icons
import { BsCreditCard, BsCalendarCheck, BsClockHistory } from "react-icons/bs";
import { IoCheckmarkCircle, IoCloseCircle } from "react-icons/io5";
import { MdPayment } from "react-icons/md";

// Billing page components
import Invoices from "layouts/billing/components/Invoices";
import Transactions from "layouts/billing/components/Transactions";

const { gradients, info, success, warning, error, text, dark } = colors;
const { card } = gradients;

// Sample subscription data
const subscriptionData = {
  plan: "Professional",
  status: "Active",
  startDate: "2024-01-15",
  endDate: "2024-12-15",
  monthlyPrice: 150,
  nextBillingDate: "2024-12-15",
  daysRemaining: 45,
};

function Billing() {
  const calculateDaysRemaining = () => {
    const today = new Date();
    const endDate = new Date(subscriptionData.endDate);
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const daysRemaining = calculateDaysRemaining();
  const progressValue = ((365 - daysRemaining) / 365) * 100;

  const handleRenewSubscription = () => {
    // Handle manual payment
    alert("Redirecting to payment page...");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        {/* Subscription Status Cards */}
        <VuiBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Current Plan", fontWeight: "regular" }}
                count={subscriptionData.plan}
                percentage={{ color: "success", text: subscriptionData.status }}
                icon={{ color: "info", component: <BsCreditCard size="22px" color="white" /> }}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Monthly Payment" }}
                count={`${subscriptionData.monthlyPrice} BHD`}
                percentage={{ color: "info", text: "/month" }}
                icon={{ color: "info", component: <MdPayment size="22px" color="white" /> }}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Days Remaining" }}
                count={daysRemaining}
                percentage={{ color: daysRemaining < 30 ? "warning" : "success", text: "days" }}
                icon={{ color: "info", component: <BsClockHistory size="22px" color="white" /> }}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Expires On" }}
                count={new Date(subscriptionData.endDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                percentage={{ color: "info", text: "Manual payment" }}
                icon={{ color: "info", component: <BsCalendarCheck size="22px" color="white" /> }}
              />
            </Grid>
          </Grid>
        </VuiBox>

        <VuiBox mb={3}>
          <Grid container spacing={3}>
            {/* Subscription Details Card */}
            <Grid item xs={12} lg={7} xl={8}>
              <Card>
                <VuiBox p={3}>
                  <VuiBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
                    <VuiTypography variant="lg" color="white" fontWeight="bold">
                      Subscription Details
                    </VuiTypography>
                    <VuiBox display="flex" alignItems="center" gap={1}>
                      {subscriptionData.status === "Active" ? (
                        <IoCheckmarkCircle color={success.main} size={20} />
                      ) : (
                        <IoCloseCircle color={error.main} size={20} />
                      )}
                      <VuiTypography
                        variant="button"
                        color={subscriptionData.status === "Active" ? "success" : "error"}
                        fontWeight="medium"
                      >
                        {subscriptionData.status}
                      </VuiTypography>
                    </VuiBox>
                  </VuiBox>

                  {/* Subscription Progress */}
                  <VuiBox mb={3}>
                    <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <VuiTypography variant="button" color="text" fontWeight="medium">
                        Subscription Progress
                      </VuiTypography>
                      <VuiTypography variant="button" color="white" fontWeight="bold">
                        {Math.round(progressValue)}%
                      </VuiTypography>
                    </VuiBox>
                    <VuiProgress
                      value={progressValue}
                      color={daysRemaining < 30 ? "warning" : "info"}
                      sx={{ background: dark.body }}
                    />
                    <VuiBox display="flex" justifyContent="space-between" mt={1}>
                      <VuiTypography variant="caption" color={text.main}>
                        Started: {new Date(subscriptionData.startDate).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                      </VuiTypography>
                      <VuiTypography variant="caption" color={text.main}>
                        Expires: {new Date(subscriptionData.endDate).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                      </VuiTypography>
                    </VuiBox>
                  </VuiBox>

                  {/* Subscription Info */}
                  <VuiBox
                    p={2}
                    mb={3}
                    sx={{
                      background: linearGradient(card.main, card.state, card.deg),
                      borderRadius: "12px",
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <VuiBox mb={2}>
                          <VuiTypography variant="caption" color={text.main} fontWeight="medium">
                            Plan Type
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="bold">
                            {subscriptionData.plan}
                          </VuiTypography>
                        </VuiBox>
                        <VuiBox>
                          <VuiTypography variant="caption" color={text.main} fontWeight="medium">
                            Monthly Price
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="bold">
                            {subscriptionData.monthlyPrice} BHD/month
                          </VuiTypography>
                        </VuiBox>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <VuiBox>
                          <VuiTypography variant="caption" color={text.main} fontWeight="medium">
                            Expiry Date
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="bold">
                            {new Date(subscriptionData.endDate).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                          </VuiTypography>
                        </VuiBox>
                      </Grid>
                    </Grid>
                  </VuiBox>

                  {/* Action Buttons */}
                  <VuiBox display="flex" gap={2} flexWrap="wrap">
                    <VuiButton
                      variant="gradient"
                      color="info"
                      onClick={handleRenewSubscription}
                      sx={{ minWidth: { xs: "100%", sm: "200px" } }}
                    >
                      Pay Now
                    </VuiButton>
                  </VuiBox>
                </VuiBox>
              </Card>
            </Grid>

            {/* Payment Summary Card */}
            <Grid item xs={12} lg={5} xl={4}>
              <Card sx={{ height: "100%" }}>
                <VuiBox p={3}>
                  <VuiTypography variant="lg" color="white" fontWeight="bold" mb={3}>
                    Payment Summary
                  </VuiTypography>
                  
                  <VuiBox
                    p={2}
                    mb={2}
                    sx={{
                      background: linearGradient(card.main, card.state, card.deg),
                      borderRadius: "12px",
                    }}
                  >
                    <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <VuiTypography variant="button" color={text.main} fontWeight="medium">
                        This Month
                      </VuiTypography>
                      <VuiTypography variant="button" color="white" fontWeight="bold">
                        {subscriptionData.monthlyPrice} BHD
                      </VuiTypography>
                    </VuiBox>
                    <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <VuiTypography variant="button" color={text.main} fontWeight="medium">
                        Total Paid
                      </VuiTypography>
                      <VuiTypography variant="button" color="white" fontWeight="bold">
                        {subscriptionData.monthlyPrice * 11} BHD
                      </VuiTypography>
                    </VuiBox>
                    <VuiBox display="flex" justifyContent="space-between" alignItems="center">
                      <VuiTypography variant="button" color={text.main} fontWeight="medium">
                        Remaining Balance
                      </VuiTypography>
                      <VuiTypography variant="button" color={success.main} fontWeight="bold">
                        0.00 BHD
                      </VuiTypography>
                    </VuiBox>
                  </VuiBox>

                </VuiBox>
              </Card>
            </Grid>
          </Grid>
        </VuiBox>

        {/* Invoices and Transactions */}
        <VuiBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={7}>
              <Invoices />
            </Grid>
            <Grid item xs={12} lg={5}>
              <Transactions />
            </Grid>
          </Grid>
        </VuiBox>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Billing;
